import { titleCase } from '@deltasierra/utilities-string';
import {
    HorizontalTextAlignment,
    VerticalTextAlignment,
    findMatchingFontMetricsOrDefault,
    letterSpacingCharacter,
} from '../../graphics/text';
import { SupportedFontSizeUnit, renderPlainText } from '../../graphics/text/plainText';
import { wrapInContextTransaction } from '../../graphics/canvasUtils';
import { getFallbackFont } from '../../utils/fontUtils';
import { TextLayer, TextFit, TextCase } from '../layers/textLayer';
import { IRenderTextOptions } from './common';

export class TextRendererV2 {
    public renderText(options: IRenderTextOptions): void {
        const { context, customFonts, layer, substituteText } = options;

        const text = this.transformTextLayerText(layer, substituteText);

        if (!text) {
            return;
        }

        const textWithCharacterSpacing = text
            .split('')
            .map((char, index) =>
                index < text.length ? `${char}${letterSpacingCharacter.repeat(layer.letterSpacing)}` : char,
            )
            .join('');

        const fallbackFontFamily = getFallbackFont(
            options.layer.text,
            options.layer.fontFamily,
            options.textToFallbackFontLink,
        );

        wrapInContextTransaction(context, () => {
            renderPlainText(context, textWithCharacterSpacing, {
                allowWordBreaks: false,
                bounds: {
                    height: layer.height,
                    width: layer.width,
                    x: 0,
                    y: 0,
                },
                fallbackFontFamily,
                fillColor: layer.fill,
                font: {
                    family: layer.fontFamily,
                    lineHeight: layer.lineHeight,
                    size: layer.fontSize,
                    sizeUnit: this.getSizeUnit(layer.fontSizeUnit),
                },
                fontMetrics: findMatchingFontMetricsOrDefault(layer.fontFamily, customFonts, fallbackFontFamily),
                horizontalAlignment: this.getHorizontalAlignment(layer.textAlign) || undefined,
                letterSpacing: layer.letterSpacing,
                shrinkToFitHeight: true,
                // Neither of the following two options are exposed in the editor
                shrinkToFitWidth: true,
                strokeColor: layer.stroke,
                strokeWidth: layer.strokeWidth,
                useOldTextRenderingLineHeightFix: options.useOldTextRenderingLineHeightFix,
                useTextRenderingLineHeightFix: options.useTextRenderingLineHeightFix,
                verticalAlignment: this.getVerticalAlignment(layer.textVerticalAlign) || undefined,
                wordWrap: layer.textFit === TextFit.wordWrap,
            });
        })();
    }

    private getSizeUnit(fontSizeUnit: string): SupportedFontSizeUnit {
        switch (fontSizeUnit) {
            case 'pt':
            case 'px':
                return fontSizeUnit as any;

            default:
                throw new Error(`Unsupported Font Size Unit: ${fontSizeUnit}`);
        }
    }

    private getHorizontalAlignment(textAlign: string | null): HorizontalTextAlignment | null {
        if (!textAlign) {
            return null;
        }
        return titleCase(textAlign) as any as HorizontalTextAlignment;
    }

    private getVerticalAlignment(textVerticalAlign: string | null): VerticalTextAlignment | null {
        if (!textVerticalAlign) {
            return null;
        }
        return titleCase(textVerticalAlign) as any as VerticalTextAlignment;
    }

    private transformTextLayerText(layer: TextLayer, substituteText: (text: string) => string): string {
        let text = substituteText(layer.text);

        if (layer.maxLength && text.length > layer.maxLength) {
            text = '';
        }

        if ('textCase' in layer) {
            switch (layer.textCase) {
                case TextCase.uppercase:
                    text = text.toUpperCase();
                    break;
                case TextCase.lowercase:
                    text = text.toLowerCase();
                    break;
                default:
                    break;
            }
        } else if (layer.uppercase) {
            // Compatibility with previous templates using only uppercase / lowercase fields
            text = text.toUpperCase();
        } else if (layer.lowercase) {
            text = text.toLowerCase();
        }

        return text;
    }
}
